export type SupportedDeepLinkEntityType =
  | "message"
  | "survey"
  | "event"
  | "poll"
  | "service"
  | "document"
  | "group"
  | "bookableAsset"
  | "ticket";
export type DeepLink = {
  url: string;
  payload:
    | {
        entityType: Exclude<SupportedDeepLinkEntityType, "document" | "group">;
        entityId: string;
        projectId: string;
        folderId?: never;
        groupTypeId?: never;
      }
    | {
        entityType: "document";
        entityId: string;
        projectId: string;
        folderId: string;
        groupTypeId?: never;
      }
    | {
        entityType: "group";
        entityId: string;
        projectId: string;
        folderId?: never;
        groupTypeId: string;
      };
};

export const SUPPORTED_DEEP_LINK_ENTITY_TYPES = [
  "message",
  "event",
  "poll",
  "service",
  "document",
  "group",
  "bookableAsset",
  "survey",
  "ticket",
] as const satisfies SupportedDeepLinkEntityType[];

export const parseDeepLink = (url: string): DeepLink["payload"] | undefined => {
  try {
    const urlObj = new URL(url);
    const type = urlObj.searchParams.get("t");
    const entityId = urlObj.searchParams.get("entity");
    const projectId = urlObj.searchParams.get("p");
    // Other ids for specific entity types
    const folderId = urlObj.searchParams.get("f"); // For document
    const groupTypeId = urlObj.searchParams.get("g"); // For group

    if (entityId && projectId && type && SUPPORTED_DEEP_LINK_ENTITY_TYPES.includes(type as any)) {
      return {
        entityType: type as SupportedDeepLinkEntityType,
        entityId: entityId,
        projectId: projectId,
        folderId: (folderId || undefined) as any,
        groupTypeId: (groupTypeId || undefined) as any,
      };
    } else {
      throw new Error("The deep link is missing required query parameters or not supported");
    }
  } catch {
    return undefined;
  }
};

export const extractDeepLinks = (content: string): DeepLink[] => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const urls = [...content.matchAll(urlRegex)].map((m) => m[0]);

  const deepLinks: DeepLink[] = [];

  for (const url of urls) {
    try {
      const urlObj = new URL(url);
      if (urlObj.pathname !== "/share-content") {
        continue; // Skip URLs that don't match the expected path
      }

      if (urlObj.search.length === 0) {
        continue; // Skip URLs that don't have query parameters
      }

      const parsedDeepLink = parseDeepLink(url);
      if (parsedDeepLink) {
        deepLinks.push({
          url,
          payload: parsedDeepLink,
        });
      }
    } catch (error) {
      // If URL parsing fails, we skip that URL and continue with the next one
    }
  }

  return deepLinks;
};

export const unfurlDeepLinkFromContent = (
  content: string,
): {
  content: string;
  entity: DeepLink | undefined;
} => {
  if (!content) {
    return {
      content,
      entity: undefined,
    };
  }

  const deepLinks = extractDeepLinks(content);
  const attachableDeepLinks = deepLinks.filter((link) => {
    const attachableEntityTypes = [
      "message",
      "bookableAsset",
      "document",
      "event",
      "group",
      "poll",
      "service",
      "survey",
    ] satisfies Exclude<(typeof SUPPORTED_DEEP_LINK_ENTITY_TYPES)[number], "ticket">[];

    return attachableEntityTypes.includes(link.payload.entityType as any);
  });

  if (attachableDeepLinks.length === 0) {
    return {
      content,
      entity: undefined,
    };
  }

  const targetDeeplink = attachableDeepLinks[0];

  let contentFinal = content;
  const contentAfterUnfurling = content.replace(targetDeeplink.url, "");
  // Only strip away the unfurled link when it's at the end of the content, to avoid breaking sentences in the middle
  if (contentAfterUnfurling.length === content.indexOf(targetDeeplink.url)) {
    contentFinal = contentAfterUnfurling;
  }

  return {
    content: contentFinal,
    entity: targetDeeplink,
  };
};

export const buildDeepLinkUrl = ({ baseUrl, payload }: { baseUrl: string; payload: DeepLink["payload"] }): string => {
  let url = `${baseUrl}/share-content`;

  const queryParams = new URLSearchParams();
  switch (payload.entityType) {
    case "message":
      queryParams.append("t", "message");
      break;

    case "bookableAsset":
      queryParams.append("t", "bookableAsset");
      break;
    case "service":
      queryParams.append("t", "service");
      break;
    case "group":
      queryParams.append("t", "group");
      queryParams.append("g", payload.groupTypeId);

      break;
    case "event":
      queryParams.append("t", "event");
      break;
    case "poll":
      queryParams.append("t", "poll");
      break;
    case "document":
      queryParams.append("t", "document");
      queryParams.append("f", payload.folderId);

      break;
    case "survey":
      queryParams.append("t", "survey");
      break;
    default:
      console.error(`Unsupported deep link entity type: ${payload.entityType}`);

      return "";
  }

  queryParams.append("entity", payload.entityId);
  queryParams.append("p", payload.projectId);

  url += `?${queryParams.toString()}`;

  return url;
};
